import React, {useState, useEffect} from 'react';
import {Outlet, useNavigate, useLocation} from 'react-router-dom';
import {useAuth} from '../context/AuthContext';
import {usePatient} from '../context/PatientContext';
import Sidebar from './Sidebar';
import TopBanner from './TopBanner';
import ChatSidebar from './ChatSidebar';
import AddPatientModal from './AddPatientModal';
import styles from './Dashboard.module.css';
import {Box, Typography, Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppFooter from './AppFooter';

const Dashboard = () => {
    const {user, logout, subscriptionStatus, loading, error} = useAuth();
    const {
        patients,
        selectedPatient,
        selectPatient,
        fetchPatients,
        isLoading: loadingPatient,
        error: errorPatient
    } = usePatient();
    const [sidebarHidden, setSidebarHidden] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [chatSidebarWidth, setChatSidebarWidth] = useState(550);
    const navigate = useNavigate();
    const location = useLocation();
    const [localLoading, setLocalLoading] = useState(false);

    const [activeView, setActiveView] = useState('notes');

    useEffect(() => {
    }, [user]);

    useEffect(() => {
        const path = location.pathname.split('/').pop();
        setActiveView(path || 'notes');
    }, [location]);

    // Check subscription status when returning from successful payment
    useEffect(() => {
        const checkSubscription = async () => {
            const params = new URLSearchParams(location.search);
            if (params.get('subscription') === 'success') {
                try {
                    setLocalLoading(true);
                    const token = await user.getIdToken();
                    await fetch('/api/check-subscription', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ update_firestore: true })
                    });
                    // Remove the subscription parameter from URL
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                } catch (error) {
                    console.error('Error checking subscription status:', error);
                } finally {
                    setLocalLoading(false);
                }
            }
        };
        
        checkSubscription();
    }, [location.search, user]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };
    
    const handleSubscribe = async () => {
        try {
            const token = await user.getIdToken();
            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to create checkout session');
            }
            
            const { url } = await response.json();
            window.location.href = url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    };

    // Check if error is related to subscription
    const isSubscriptionError = error && (
        error.includes('subscription') || 
        error.includes('Subscription') || 
        error.toLowerCase().includes('expired')
    );

    if (loading) return <div className={styles.dashboardLoading}>Loading...</div>;
    
    if (error) {
        if (isSubscriptionError) {
            return (
                <div className={styles.dashboardContainer}>
                    <TopBanner
                        activeView={activeView}
                        setActiveView={setActiveView}
                        userName={user?.displayName || 'User'}
                        toggleSidebar={toggleSidebar}
                        onLogout={handleLogout}
                    />
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        height="calc(100vh - 80px)"
                        p={3}
                    >
                        <Typography variant="h4" color="error" gutterBottom>
                            Subscription Required
                        </Typography>
                        <Typography variant="body1" gutterBottom paragraph sx={{ maxWidth: 600, textAlign: 'center', mb: 4 }}>
                            {error}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<CreditCardIcon />}
                            onClick={handleSubscribe}
                        >
                            Renew Subscription
                        </Button>
                    </Box>
                </div>
            );
        }
        return <div className={styles.dashboardError}>Error: {error}</div>;
    }
    
    if (!user) return <div className={styles.dashboardError}>Error: User data is missing. Please log in.</div>;
    if (!Array.isArray(patients)) {
        return <div className={styles.dashboardLoading}>Loading client data...</div>;
    }

    const toggleSidebar = () => setSidebarHidden(!sidebarHidden);
    const handlePatientSelection = (patient) => selectPatient(patient);
    const handleAddPatient = () => setModalVisible(true);
    const handleCloseModal = () => setModalVisible(false);
    const handlePatientAdded = (newPatient) => {
        fetchPatients();
        setModalVisible(false);
    };

    const handleSetActiveView = (view) => {
        setActiveView(view);
        navigate(`/dashboard/${view}`, { replace: true });
    };

    const handleChatSidebarResize = (newWidth) => {
        setChatSidebarWidth(newWidth);
    };

    return (
        <div className={styles.dashboardContainer}>
            <TopBanner
                activeView={activeView}
                setActiveView={handleSetActiveView}
                userName={user.displayName}
                toggleSidebar={toggleSidebar}
                onLogout={handleLogout}
            />
            <div
                className={styles.dashboardContent}
                style={{
                    "--chat-sidebar-width": `${chatSidebarWidth}px`,
                    "--sidebar-width": sidebarHidden ? "0px" : "250px"
                }}
            >
                <div
                    className={`${styles.dashboardSidebarContainer} ${sidebarHidden ? styles.dashboardSidebarHidden : ''}`}>
                    <Sidebar
                        onSelectPatient={handlePatientSelection}
                        onAddPatient={handleAddPatient}
                    />
                </div>
                <div className={styles.dashboardMainContentWrapper}>
                    <div className={styles.dashboardMainContent}>
                        {activeView === 'settings' ? (
                            <Outlet/>
                        ) : patients.length === 0 ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                height="100%"
                            >
                                <Typography variant="h5" gutterBottom>
                                    Welcome to Psyntel!
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    To get started, add your first client.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon/>}
                                    onClick={handleAddPatient}
                                    sx={{mt: 2}}
                                >
                                    Add Client
                                </Button>
                            </Box>
                        ) : selectedPatient ? (
                            <Outlet/>
                        ) : (
                            <div className={styles.dashboardNoPatientSelected}>Please select a client from the
                                sidebar.</div>
                        )}
                        <AppFooter/>
                    </div>
                </div>
                {activeView !== 'settings' && (
                    <ChatSidebar
                        onResize={handleChatSidebarResize}
                        initialWidth={chatSidebarWidth}
                        style={{height: 'calc(100vh - 80px)'}}

                    />
                )}
            </div>
            <AddPatientModal
                show={modalVisible}
                handleClose={handleCloseModal}
                onPatientAdded={handlePatientAdded}
            />
        </div>
    );
};

export default Dashboard;